import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { api } from '../api';
import { toast } from '../../hooks/use-toast';
import { errorMessage } from '../errors';

const fmtInt = (n) => Number(n || 0).toLocaleString('en-US');

// "2026-10" -> "October 2026"
const monthLabel = (period) => {
  const [y, m] = String(period || '').split('-').map(Number);
  if (!y || !m) return 'This month';
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
};

export default function AiUsageTab() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/v2/ai/usage')
      .then(({ data: d }) => setData(d))
      .catch((e) => toast({ title: 'Could not load AI usage', description: errorMessage(e) }));
  }, []);

  if (!data) {
    return (
      <div className="p-8 flex items-center justify-center text-slate-400" data-testid="ai-usage-loading">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading...
      </div>
    );
  }

  const used = data.usage?.tokens || 0;
  const limit = data.limit || 0;
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const full = limit > 0 && used >= limit;
  const barColor = full ? '#DC2626' : pct >= 80 ? '#D97706' : '#6BA82E';

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 max-w-lg space-y-4" data-testid="ai-usage-tab">
      <div>
        <h2 className="font-serif text-lg font-bold" style={{ color: '#070B5F' }}>AI usage</h2>
        <p className="text-sm text-slate-500 mt-1">
          {monthLabel(data.usage?.period)}. Counts Audrey summaries and questions about your contacts and deals.
        </p>
      </div>
      <div>
        <div className="flex items-baseline justify-between text-sm">
          <span className="font-medium" data-testid="ai-usage-figures">{fmtInt(used)} of {fmtInt(limit)} tokens</span>
          <span className="text-slate-500" data-testid="ai-usage-pct">{pct}%</span>
        </div>
        <div className="mt-2 h-2.5 rounded-full bg-slate-100 overflow-hidden" role="progressbar"
          aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="AI tokens used this month">
          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: barColor }} />
        </div>
        {full && (
          <p className="text-sm text-red-600 mt-2" data-testid="ai-usage-full">
            The monthly limit is reached. Summaries stop until next month.
          </p>
        )}
      </div>
      <div className="text-sm text-slate-500" data-testid="ai-usage-actions">
        {fmtInt(data.usage?.actions)} AI {data.usage?.actions === 1 ? 'request' : 'requests'} this month
      </div>
    </div>
  );
}
